import { SyntheticEvent, useEffect, useState } from 'react';

import { CfsApi } from '@/api';
import { SearchInput, Spinner } from '@/components/TailwindComponents';
import { ICFS, IncomingCallCFSFormValues } from '@/models';
import { useToastStore } from '@/store';

import { RecentCFSCard } from './RecentCFSCard';

interface RecentCFSListProps {
  incomingCallId: string;
  onClose: () => void;
  getValues: () => Partial<Pick<IncomingCallCFSFormValues, 'notes'>>;
}

const PAGE_SIZE = 20;

export const RecentCFSList = ({
  incomingCallId,
  onClose,
  getValues,
}: RecentCFSListProps) => {
  const [cfsList, setCfsList] = useState<ICFS[]>([]);
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');

  const { updateToast } = useToastStore();

  useEffect(() => {
    fetchCFSs();
  }, [page, searchText]);

  const fetchCFSs = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: PAGE_SIZE,
        sort: JSON.stringify({ createdAt: -1 }),
        filter: searchText
          ? JSON.stringify({
              items: [],
              logicOperator: 'and',
              quickFilterValues: [searchText],
              quickFilterLogicOperator: 'and',
            })
          : undefined,
      };

      const response = await CfsApi.list(params);
      const { docs, hasNextPage: nextPage } = response.data;

      setCfsList((prev) => (page === 1 ? docs : [...prev, ...docs]));
      setHasNextPage(nextPage);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value: string) => {
    if (value === searchText) return;
    setSearchText(value);
    setPage(1);
    setCfsList([]);
  };

  const handleScroll = (event: SyntheticEvent) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const scrollThreshold = 50;

    if (
      scrollTop + clientHeight >= scrollHeight - scrollThreshold &&
      !loading &&
      hasNextPage
    ) {
      setPage((prev) => prev + 1);
    }
  };

  return (
    <div className="flex flex-col h-full w-full">
      <div className="px-4 mb-3 w-full">
        <div className="w-full [&>div]:w-full">
          <SearchInput
            placeholder="Search..."
            value={searchText}
            onChange={handleSearchChange}
            debounceTime={500}
          />
        </div>
      </div>

      <div
        className="recent-cfs flex flex-col overflow-y-auto flex-1 w-full"
        onScroll={handleScroll}
      >
        {cfsList.map((item) => (
          <RecentCFSCard
            key={item._id}
            {...item}
            incomingCallId={incomingCallId}
            disableClose={true}
            onClose={onClose}
            getValues={getValues}
          />
        ))}

        {loading && (
          <div className="flex justify-center py-4">
            <Spinner size="small" />
          </div>
        )}

        {!loading && cfsList.length === 0 && (
          <p className="text-center text-gray-500 py-8">No CFSs found</p>
        )}
      </div>
    </div>
  );
};
